import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Row,
  Input,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  FormGroup,
  Label,
} from "reactstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import { useNavigate } from "react-router-dom";
import AgentService from "../../../services/KangarooServices/AgentService";
import CustomDataTable from "../../../Components/CustomTable/CustomTable";
import toast from "react-hot-toast";
import DeleteModal from "../../../Components/Common/DeleteModal";

const Agent = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterText, setFilterText] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editingTag, setEditingTag] = useState(null);
  const [isOpenDelete, setIsOpenDelete] = useState(false);
  const [selectedDeleteItem, setSelectedDeleteItem] = useState(null);
  const navigate = useNavigate();
  const toggleDelete = () => setIsOpenDelete(!isOpenDelete);

  // Fetch all tags
  const fetchData = async () => {
    try {
      setLoading(true);
      const result = await AgentService.getList();
      setData(result);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const toggleModal = (tag = null) => {
    setEditingTag(tag);
    setModalOpen(!modalOpen);
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: editingTag ? editingTag.name : "",
      email: editingTag ? editingTag?.email : "",
      phoneNumber: editingTag ? editingTag?.phoneNumber : "",
      isActive: editingTag ? editingTag?.isActive : true,
    },
    validationSchema: Yup.object({
      name: Yup.string().required("Agent name is required"),
      phoneNumber: Yup.string()
        .matches(/^\d+$/, "Phone must be a number")
        .required("Phone number is required"),
      email: Yup.string().email("Invalid email").required("Email is required"),
    }),
    onSubmit: async (values, { resetForm }) => {
      try {
        if (editingTag?.id) {
          await AgentService.update(editingTag.id, values);
          toast.success("Agent updated succesfull..");
        } else {
          await AgentService.create(values);
          toast.success("Agent created succesfull..");
        }
        fetchData();
        toggleModal(null);
        resetForm();
      } catch (error) {
        console.log(error);
      }
    },
  });

  const customStyles = {
    headCells: { style: { fontSize: "0.9rem", fontWeight: 600 } },
    rows: { style: { fontSize: "0.9rem" } },
  };

  const filteredData = data.filter((item) =>
    (item.name || "").toLowerCase().includes(filterText.toLowerCase())
  );

  const columns = [
    {
      name: "S. No",
      cell: (row, index) => <div>{index + 1}</div>,
      width: "90px",
      center: true,
    },
    {
      name: "Name",
      cell: (row) => (
        <span
          style={{
            cursor: "pointer",
            color: "#0d6efd",
            textDecoration: "none",
          }}
          onClick={() => navigate(`/tag/detail/${row.id}`)}
        >
          {row.name}
        </span>
      ),
      sortable: true,
    },
    {
      name: "Email",
      selector: (row) => row.email || "not found",

      sortable: true,
    },
    {
      name: "Phone Number",
      selector: (row) => row.phoneNumber || "not found",

      sortable: true,
    },
    {
      name: "Actions",
      center: true,
      cell: (row) => (
        <div>
          <Button
            size="sm"
            color="warning"
            className="btn-soft-warning me-2 p-1"
            onClick={() => toggleModal(row)}
          >
            <i className="ri-pencil-line"></i>
          </Button>
          <Button
            size="sm"
            color="danger"
            className="btn-soft-danger p-1"
            onClick={() => handleDeleteClick(row?.id)}
          >
            <i className="ri-delete-bin-5-line"></i>
          </Button>
        </div>
      ),
    },
  ];

  // delete
  const handleDeleteClick = (item) => {
    setSelectedDeleteItem(item);
    toggleDelete();
  };

  const handleDelete = async () => {
    if (selectedDeleteItem) {
      try {
        await AgentService.delete(selectedDeleteItem);
        fetchData();
        toast.success("Agent deleted successfully");
      } catch (error) {
        toast.error("Delete failed");
      } finally {
        toggleDelete();
        setSelectedDeleteItem(null);
      }
    }
  };

  return (
    <Container fluid>
      <div className="page-content">
        <BreadCrumb title="Tags" pageTitle="Agent management" />

        <Row className="g-4 mb-4 d-flex justify-content-between">
          <Col className="col-sm-auto">
            <Button color="success" onClick={() => toggleModal(null)}>
              <i className="ri-add-line align-bottom me-1"></i> Add Agent
            </Button>
          </Col>
          <Col md={6} sm="4">
            <Input
              type="text"
              placeholder="Search by agent name..."
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
            />
          </Col>
        </Row>

        {/* Desktop Table */}
        <Card className="d-none d-lg-block">
          <CardBody>
            <CustomDataTable
              columns={columns}
              data={filteredData}
              customStyles={customStyles}
              pagination
              striped
              responsive
              fixedHeader
              progressPending={loading}
            />
          </CardBody>
        </Card>

        {/* Modal for Add/Edit */}
        <Modal isOpen={modalOpen} toggle={() => toggleModal(null)}>
          <ModalHeader toggle={() => toggleModal(null)}>
            {editingTag ? "Update Tag" : "Add Agent"}
          </ModalHeader>
          <ModalBody>
            <form onSubmit={formik.handleSubmit}>
              <FormGroup>
                <Label for="name">Agent Name</Label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  placeholder="Enter tag name"
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  invalid={formik.touched.name && Boolean(formik.errors.name)}
                />
                {formik.touched.name && formik.errors.name && (
                  <div className="text-danger mt-1">{formik.errors.name}</div>
                )}
              </FormGroup>
              <FormGroup>
                <Label for="email">Email Address</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="Enter the email"
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  invalid={formik.touched.email && Boolean(formik.errors.email)}
                />
                {formik.touched.email && formik.errors.email && (
                  <div className="text-danger mt-1">{formik.errors.email}</div>
                )}
              </FormGroup>
              <FormGroup>
                <Label for="phoneNumber">Phone Number</Label>
                <Input
                  id="phoneNumber"
                  name="phoneNumber"
                  value={formik.values.phoneNumber}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  invalid={
                    formik.touched.phoneNumber && !!formik.errors.phoneNumber
                  }
                />
                {formik.touched.phoneNumber && formik.errors.phoneNumber && (
                  <div className="text-danger mt-1">
                    {formik.errors.phoneNumber}
                  </div>
                )}
              </FormGroup>

              <ModalFooter>
                <Button color="primary" type="submit">
                  {editingTag ? "Update" : "Add"}
                </Button>
                <Button color="secondary" onClick={() => toggleModal(null)}>
                  Cancel
                </Button>
              </ModalFooter>
            </form>
          </ModalBody>
        </Modal>
      </div>
      <DeleteModal
        show={isOpenDelete}
        onCloseClick={toggleDelete}
        onDeleteClick={handleDelete}
      />
    </Container>
  );
};

export default Agent;
